import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { askSessionPath } from '@/constants/appRoutes';
import {
  documentApi,
  ChatMessage,
  ContextItem,
  Document,
  RAGCitation,
} from '@/services/api';
import { useChatSessions } from '@/hooks/useChatSessions';
import { useProjects } from '@/hooks/useProjects';
import { useChatContext } from './hooks/useChatContext';
import { useChatConversation } from './hooks/useChatConversation';
import { useChatShell } from './ChatShellContext';
import { ChatMessages } from './components/ChatMessages';
import { AskCommunityLayout } from './components/AskCommunityLayout';
import { ChatComposer } from './components/ChatComposer';
import { ChatEmptyState } from './components/ChatEmptyState';

const CHAT_MAX_WIDTH = 720;

export function AskEmptyPage() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const navigate = useNavigate();
  const { askMode, setShowSources, setCitations } = useChatShell();
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const submittingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [preUploadedIds, setPreUploadedIds] = useState<string[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();

  const setSidebarCitations = useCallback(
    (citations: RAGCitation[]) => {
      setCitations(citations);
      setShowSources(citations.length > 0);
    },
    [setCitations, setShowSources],
  );

  useEffect(() => {
    setShowSources(false);
    setCitations([]);
  }, [setShowSources, setCitations]);

  const {
    contextMode,
    selectedDocs,
    setSelectedDocs,
    selectedChats,
    setSelectedChats,
    selectedProjects,
    setSelectedProjects,
    attachedFiles,
    contextLocked,
    setContextLocked,
    uploadStatusLines,
    setUploadStatusLines,
    handleFileSelect,
    removeAttachedFile,
    removeSelectedDoc,
    removeSelectedChat,
    removeSelectedProject,
  } = useChatContext();
  const { sessions: allSessions } = useChatSessions();
  const { allProjects, loading: projectsLoading } = useProjects();

  useEffect(() => {
    const loadPending = async () => {
      const pendingContext = sessionStorage.getItem('pendingContextItems');
      if (!pendingContext) {
        setInitialDataLoaded(true);
        return;
      }

      try {
        const contextItems: ContextItem[] = JSON.parse(pendingContext);
        const chatIds = contextItems
          .filter((item) => item.context_type === 'previous_chat' && item.referenced_session_id)
          .map((item) => item.referenced_session_id!);

        if (chatIds.length > 0 && allSessions.length === 0) {
          return;
        }

        const projectIds = contextItems
          .filter((item) => item.context_type === 'project' && (item as ContextItem & { project_id?: string }).project_id)
          .map((item) => (item as ContextItem & { project_id: string }).project_id);

        if (projectIds.length > 0 && allProjects.length === 0 && projectsLoading) {
          return;
        }

        const docIds = contextItems
          .filter((item) => item.context_type === 'document' && item.document_id)
          .map((item) => item.document_id!);
        if (docIds.length > 0) {
          const docs = await Promise.all(
            docIds.map((id) => documentApi.getDocument(id).catch(() => null)),
          );
          setSelectedDocs(docs.filter((d): d is Document => d !== null));
        }
        if (chatIds.length > 0) {
          setSelectedChats(allSessions.filter((s) => chatIds.includes(s.id)));
        }
        if (projectIds.length > 0) {
          setSelectedProjects(allProjects.filter((p) => projectIds.includes(p.id)));
        }
        const uploadDocIds = contextItems
          .filter((item) => item.context_type === 'upload' && item.document_id)
          .map((item) => item.document_id!);
        if (uploadDocIds.length > 0) setPreUploadedIds(uploadDocIds);
        sessionStorage.removeItem('pendingContextItems');
      } catch (e) {
        console.error('Failed to parse pending context items', e);
      }
      setInitialDataLoaded(true);
    };
    void loadPending();
  }, [setSelectedDocs, setSelectedChats, setSelectedProjects, allSessions, allProjects, projectsLoading]);

  const navigateToSession = useCallback(
    (sessionId: string, options?: { replace?: boolean; state?: unknown }) => {
      navigate(askSessionPath(sessionId), { replace: options?.replace, state: options?.state });
    },
    [navigate],
  );

  const { handleSubmit, handleKeyDown } = useChatConversation({
    initialQuery,
    initialDataLoaded,
    sessionId,
    setSessionId,
    navigateToSession,
    locationState: null,
    currentSessionRole: 'owner',
    messages,
    setMessages,
    input,
    setInput,
    loading,
    setLoading,
    streamingMessageId,
    setStreamingMessageId,
    setStreamStatus,
    sidebarCitations: [],
    setSidebarCitations,
    contextMode,
    selectedDocs,
    selectedChats,
    selectedProjects,
    attachedFiles,
    preUploadedIds,
    setContextLocked,
    uploadStatusLines,
    setUploadStatusLines,
    messagesEndRef,
    submittingRef,
  });

  const chatInput = (
      <ChatComposer
        input={input}
        onInputChange={setInput}
        onAiSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        currentSessionRole="owner"
        sessionId={sessionId}
        loading={loading}
        contextLocked={contextLocked}
        selectedDocs={selectedDocs}
        selectedChats={selectedChats}
        selectedProjects={selectedProjects}
        attachedFiles={attachedFiles}
        onRemoveDoc={removeSelectedDoc}
        onRemoveChat={removeSelectedChat}
        onRemoveProject={removeSelectedProject}
        onRemoveFile={removeAttachedFile}
        uploadStatusLines={uploadStatusLines}
        fileInputRef={fileInputRef}
        onFileSelect={handleFileSelect}
        autoFocus={messages.length === 0 && !loading}
        prominent={messages.length === 0 && !loading && askMode !== 'community'}
      />
  );

  if (askMode === 'community') {
    return <AskCommunityLayout>{chatInput}</AskCommunityLayout>;
  }

  if (messages.length === 0 && !loading) {
    return (
      <ChatEmptyState
        onSelectPrompt={(prompt) => {
          setInput(prompt);
        }}
      >
        {chatInput}
      </ChatEmptyState>
    );
  }

  return (
    <>
      <ChatMessages
        messages={messages}
        loading={loading}
        streamingMessageId={streamingMessageId}
        streamStatus={streamStatus}
        messagesEndRef={messagesEndRef}
        maxWidth={CHAT_MAX_WIDTH}
      />
      {chatInput}
    </>
  );
}
